/**
 * Character Customizer — catalog, rarity ladder, layer order, focus maps.
 * Economy is present in the UI but free for now: no prices, no locked items.
 */

import type {
  CustomizerCategory,
  CustomizerCategoryId,
  EquippedMap,
  FocusView,
  RarityId,
  RarityMeta,
} from './characterCustomizer.types';

/** Bespoke dark-purple surfaces specific to the customizer (not in the global theme). */
export const CUSTOMIZER_SURFACE = {
  stageBg: '#0F0A1A',
  shopBg: '#15101F',
  railBorder: 'rgba(255,255,255,0.05)',
  divider: 'rgba(255,255,255,0.06)',
  cellBg: '#211B2E',
  cellBgActive: 'rgba(167,139,250,0.12)',
  railActiveBg: 'rgba(167,139,250,0.16)',
  iconBtnBg: '#262130',
  surpriseBg: '#262130',
  surpriseBorder: 'rgba(167,139,250,0.3)',
  coinPillBg: '#15101F',
  thumbTop: '#241D33',
  thumbBottom: '#15101F',
  collectionBg: 'rgba(31,41,55,0.65)',
  collectionBorder: 'rgba(255,255,255,0.08)',
} as const;

export const RARITY: Record<RarityId, RarityMeta> = {
  common: { label: 'Common', color: '#9CA3AF', glow: 'rgba(156,163,175,0)' },
  rare: { label: 'Rare', color: '#6EE7B7', glow: 'rgba(110,231,183,0.45)' },
  epic: { label: 'Epic', color: '#A78BFA', glow: 'rgba(167,139,250,0.55)' },
  legendary: { label: 'Legendary', color: '#FCD34D', glow: 'rgba(252,211,77,0.6)' },
};

/**
 * Catalog. Every accessory PNG is authored in the same 500×500 frame, so layers
 * stack by absolute fill. Asset keys resolve under `assets/character/` on the CDN.
 * Base ships with Avatar 1 only for now (Avatar 2 added later). Gloves omitted (no art yet).
 */
export const CATALOG: Record<CustomizerCategoryId, CustomizerCategory> = {
  base: {
    id: 'base',
    label: 'Base',
    removable: false,
    items: [
      { id: 'striker', name: 'The Striker', assetKey: 'char/avatar1', rarity: 'common' },
      { id: 'avatar2', name: 'The Playmaker', assetKey: 'char/avatar2', rarity: 'common' },
      { id: 'avatar3', name: 'The Keeper', assetKey: 'char/avatar3', rarity: 'rare' },
      { id: 'avatar4', name: 'The Defender', assetKey: 'char/avatar4', rarity: 'rare' },
      { id: 'avatar5', name: 'The Maestro', assetKey: 'char/avatar5', rarity: 'epic' },
      { id: 'avatar6', name: 'The Sweeper', assetKey: 'char/avatar6', rarity: 'rare' },
      { id: 'avatar7', name: 'The Wing-Back', assetKey: 'char/avatar7', rarity: 'rare' },
      { id: 'avatar8', name: 'The Libero', assetKey: 'char/avatar8', rarity: 'epic' },
      { id: 'girl1', name: 'The Forward', assetKey: 'char/girl1', rarity: 'rare' },
      { id: 'girl2', name: 'The Winger', assetKey: 'char/girl2', rarity: 'epic' },
      { id: 'girl3', name: 'The Captain', assetKey: 'char/girl3', rarity: 'epic' },
    ],
  },
  hair: {
    id: 'hair',
    label: 'Hair',
    removable: true,
    items: [
      { id: 'blonde', name: 'Blonde Mohawk', assetKey: 'hair/mohawk-blonde', rarity: 'rare' },
      { id: 'brown', name: 'Brown Mohawk', assetKey: 'hair/mohawk-brown', rarity: 'rare' },
      { id: 'sideponytail', name: 'Side Ponytail', assetKey: 'hair/side-ponytail', rarity: 'epic' },
    ],
  },
  hat: {
    id: 'hat',
    label: 'Hats',
    removable: true,
    items: [
      { id: 'red', name: 'Classic Red', assetKey: 'hats/red', rarity: 'common' },
      { id: 'whitered', name: 'White & Red', assetKey: 'hats/whitered', rarity: 'common' },
      { id: 'lightblue', name: 'Sky Blue Cap', assetKey: 'hats/lightblue', rarity: 'common' },
      { id: 'argentina', name: 'Albiceleste', assetKey: 'hats/argentina', rarity: 'rare' },
      { id: 'australia', name: 'Socceroos Cap', assetKey: 'hats/australia', rarity: 'rare' },
      { id: 'brazil', name: 'Seleção Cap', assetKey: 'hats/brazil', rarity: 'epic' },
      { id: 'belgium', name: 'Red Devils Cap', assetKey: 'hats/belgium', rarity: 'epic' },
      { id: 'france', name: 'Les Bleus Cap', assetKey: 'hats/france', rarity: 'epic' },
      { id: 'spain', name: 'La Roja Cap', assetKey: 'hats/spain', rarity: 'epic' },
      { id: 'portugal', name: 'Seleção das Quinas', assetKey: 'hats/portugal', rarity: 'epic' },
      { id: 'usa', name: 'Stars & Stripes', assetKey: 'hats/usa', rarity: 'legendary' },
    ],
  },
  scarf: {
    id: 'scarf',
    label: 'Scarves',
    removable: true,
    items: [
      { id: 'yellowblack', name: 'Home Stripes', assetKey: 'scarfs/yellowblack', rarity: 'common' },
      { id: 'greywhite', name: 'Grey & White', assetKey: 'scarfs/greywhite', rarity: 'common' },
      { id: 'redwhite', name: 'Red & White', assetKey: 'scarfs/redwhite', rarity: 'rare' },
      { id: 'lightbluewhite', name: 'Sky Blue Scarf', assetKey: 'scarfs/lightbluewhite', rarity: 'rare' },
      { id: 'brazil', name: 'Canarinho', assetKey: 'scarfs/brazil', rarity: 'legendary' },
    ],
  },
  sleeves: {
    id: 'sleeves',
    label: 'Sleeves',
    removable: true,
    items: [
      { id: 'black', name: 'Black Sleeves', assetKey: 'sleeves/black', rarity: 'common' },
      { id: 'red', name: 'Red Sleeves', assetKey: 'sleeves/red', rarity: 'rare' },
      { id: 'azul', name: 'Blue Sleeves', assetKey: 'sleeves/azul', rarity: 'rare' },
      { id: 'green', name: 'Green Sleeves', assetKey: 'sleeves/green', rarity: 'rare' },
      { id: 'yellow', name: 'Yellow Sleeves', assetKey: 'sleeves/yellow', rarity: 'rare' },
    ],
  },
  pants: {
    id: 'pants',
    label: 'Shorts',
    removable: true,
    items: [
      { id: 'black', name: 'Black Shorts', assetKey: 'pants/black', rarity: 'common' },
      { id: 'red', name: 'Red Shorts', assetKey: 'pants/red', rarity: 'rare' },
    ],
  },
  shoes: {
    id: 'shoes',
    label: 'Boots',
    // Boots are always worn: no "no shoes" option (no clear cell, can't unequip, surprise-me
    // always picks a boot). Server stays permissive (shoes not in NON_REMOVABLE) so older
    // clients / existing shoes:null configs still save.
    removable: false,
    items: [
      { id: 'blackwhite', name: 'Classic Boots', assetKey: 'shoes/blackwhite', rarity: 'common' },
      { id: 'bluewhite', name: 'Ice Boots', assetKey: 'shoes/bluewhite', rarity: 'epic' },
      { id: 'yellow', name: 'Gold Boots', assetKey: 'shoes/yellow', rarity: 'legendary' },
      { id: 'white', name: 'Pure Whites', assetKey: 'shoes/white', rarity: 'common' },
      { id: 'pink', name: 'Pink Flash', assetKey: 'shoes/pink', rarity: 'rare' },
      { id: 'purple', name: 'Violet Strike', assetKey: 'shoes/purple', rarity: 'epic' },
      { id: 'gold', name: 'Golden Cleats', assetKey: 'shoes/gold', rarity: 'legendary' },
    ],
  },
};

/**
 * Base avatars that ship with their own hairstyle. The separate hair layer is
 * authored for the male frame and looks wrong on these, so hair is hidden and
 * force-cleared whenever one of them is the equipped base.
 */
export const WOMEN_BASE_IDS: ReadonlySet<string> = new Set(['girl1', 'girl2', 'girl3']);

/** Rail order (left vertical strip). */
export const CATS: CustomizerCategoryId[] = [
  'base',
  'hair',
  'hat',
  'scarf',
  'sleeves',
  'pants',
  'shoes',
];

/**
 * Back → front paint order for the layered avatar. Boots paint BEHIND the base so the
 * base's socks/legs sit over the boot opening (sock-over-boot); the boot toe still shows
 * below the feet. Everything else paints over the base.
 */
export const LAYER_ORDER: CustomizerCategoryId[] = [
  'shoes',
  'base',
  'pants',
  'sleeves',
  'hair',
  'scarf',
  'hat',
];

/** Default look the customizer opens with. */
export const DEFAULT_FIT: EquippedMap = {
  base: 'striker',
  hair: null,
  hat: null,
  scarf: 'yellowblack',
  sleeves: null,
  pants: 'black',
  shoes: 'yellow',
};

/** Decorative starting coin balance (display-only until the economy is wired). */
export const INITIAL_COINS = 1250;

/** Crop focus per category for cell previews — region of the 500-frame to zoom onto. */
export const FOCUS: Record<CustomizerCategoryId, FocusView> = {
  base: { cx: 0.5, cy: 0.5, scale: 1.0 },
  hair: { cx: 0.5, cy: 0.23, scale: 2.05 },
  hat: { cx: 0.5, cy: 0.3, scale: 1.85 },
  scarf: { cx: 0.5, cy: 0.54, scale: 1.7 },
  sleeves: { cx: 0.5, cy: 0.5, scale: 1.5 },
  pants: { cx: 0.5, cy: 0.62, scale: 1.95 },
  shoes: { cx: 0.5, cy: 0.85, scale: 1.95 },
};
